import classNames from 'classnames'
import { Controller } from 'react-hook-form'

import type { ReactElement } from 'react'
import type { Control, UseFormRegister } from 'react-hook-form'
import type { TCategoryId, TClusterForm, TFormCategory, TFormStatus, TStatus } from '~/types/cluster'
import type { MobileDeviceType } from '~/types/device-type'
import type { FormValues } from '~/views/Competitors/Clusters/ClusterAppInfos/InfosForm'

import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import FileUploadInput from '~/components/Inputs/ReactHookForm/FileUploadInput'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import MultiTextInput from '~/components/Inputs/ReactHookForm/MultiTextInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'

export const getFormStatusEmoji = (status: TStatus | null | undefined): string => {
  switch (status) {
    case 'todo':
      return '🔴'
    case 'in_progress':
      return '🟠'
    case 'done':
      return '🟢'
    default:
      return '❔'
  }
}

export const forgeDataAiUrl = (deviceType?: MobileDeviceType, storeId?: string): string | undefined => {
  switch (deviceType) {
    case 'android':
      return `https://www.data.ai/apps/google-play/app/${storeId}/details`
    case 'ios':
      return `https://www.data.ai/apps/ios/app/${storeId}/details`
    default:
      return undefined
  }
}

export const getInputs = (
  category: TCategoryId,
  data?: TClusterForm[]
):
  | {
      category: TCategoryId
      initialValues: Record<string, string | boolean | null>
      inputs: TClusterForm[]
    }
  | undefined => {
  if (data) {
    const initialValues: { [key: string]: any } = {}

    for (const field of data) {
      initialValues[field.id] = field.value
    }

    return {
      category,
      initialValues: initialValues,
      inputs: data,
    }
  }
  return
}

export const getFormTabs = (
  statuses?: TFormStatus,
  tabOrders?: string[]
):
  | {
      label: string
      value: TStatus | null
    }[]
  | undefined => {
  if (statuses && tabOrders) {
    return tabOrders.map((tab) => ({
      label: tab,
      value: statuses[`status_${tab}`],
    }))
  }
  return
}

// Both const used in the incoming form values function
const flattenedObject: Record<string, any> = {}

const flatten = (obj: any, parentKey: string = '') => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key

      const value = obj[key]

      if (Array.isArray(value)) {
        value.forEach((item: TClusterForm) => {
          if (item && item.id && item.value !== undefined) {
            flattenedObject[item.id] = item.value
          }
        })
      } else if (value && typeof value === 'object') {
        flatten(value, newKey)
      } else {
        flattenedObject[key] = value
      }
    }
  }
}

// Function to initialize AppInfos form values based on form API response
export const getFormValuesfromFormData = (input?: TFormCategory): Record<string, unknown> => {
  if (Array.isArray(input)) {
    return input.reduce((acc: Record<string, string | string[] | boolean>, feature: TClusterForm) => {
      if (feature && feature.id && feature.value !== undefined) {
        acc[feature.id] = feature.value
      }
      return acc
    }, {})
  } else if (input !== null && typeof input === 'object') {
    flatten(input)

    return flattenedObject
  }

  return {}
}

const getSelectOptions = (options?: string[] | null) =>
  options ? options.map((option) => ({ label: option, value: option })) : []

export const renderFormContent = ({
  control,
  data,
  register,
  selectedCategory,
}: {
  control: Control<FormValues, any, FormValues>
  data?: TFormCategory
  register: UseFormRegister<FormValues>
  selectedCategory?: TCategoryId
}): ReactElement | null => {
  if (data && selectedCategory) {
    const subCategories = [...new Map(data[selectedCategory].map((v) => [v.sub_category, v])).values()].map(
      (item) => item.sub_category
    )

    subCategories.push(subCategories.splice(subCategories.indexOf(''), 1)[0])

    return (
      <section className={classNames('main', { 'multi-categories': subCategories.length > 1 })}>
        {subCategories.map((subCat, index) => (
          <div
            className={classNames('form-category-wrapper', { 'single-category': subCategories.length === 1 })}
            key={`${subCat}-${index}`}
          >
            <span className="category-title">{subCat ? subCat.split('_').join(' ') : 'No category'}</span>
            {data[selectedCategory]
              .filter(({ sub_category }) => sub_category === subCat)
              .map(({ id, input_type, label, position, select_options, value }) => {
                const options = getSelectOptions(select_options)
                let input

                switch (input_type) {
                  case 'FILE':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={() => (
                          <FileUploadInput
                            accept={{ 'image/*': [], 'video/*': [] }}
                            label={label}
                            value={value}
                            {...register(id)}
                          />
                        )}
                      />
                    )
                    break
                  case 'TEXT':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={() => (
                          <TextInput
                            className="text"
                            defaultValue={value}
                            key={id}
                            label={label}
                            labelPosition="left"
                            placeholder={`Enter ${label}`}
                            {...register(id)}
                          />
                        )}
                      />
                    )
                    break
                  case 'TEXTAREA':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={() => (
                          <TextArea
                            className="text-area"
                            defaultValue={value}
                            key={id}
                            label={label}
                            labelPosition="left"
                            placeholder={`Enter ${label}`}
                            resizable
                            {...register(id)}
                          />
                        )}
                      />
                    )
                    break
                  case 'MULTI TEXT':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={({ field: { onChange } }) => (
                          <MultiTextInput
                            className="infos-form__main__input__multi-text"
                            defaultValue={value.map((value) => ({ disabled: false, value }))}
                            label={label}
                            onChange={onChange}
                            placeholder={`Enter ${label}`}
                          />
                        )}
                      />
                    )
                    break
                  case 'BOOL':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={({ field: { onChange } }) => (
                          <CheckboxInput
                            defaultChecked={value}
                            label={label}
                            labelPosition="left"
                            onChange={onChange}
                          />
                        )}
                      />
                    )
                    break
                  case 'SELECT':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={({ field: { onChange, value: formValue } }) => (
                          <SelectInput
                            className="select"
                            label={label}
                            labelPosition="left"
                            onChange={(option) => onChange((option as IOption).value)}
                            options={options}
                            value={options.find((option) => option.value === formValue)}
                          />
                        )}
                      />
                    )
                    break
                  case 'MULTI SELECT':
                    input = (
                      <Controller
                        control={control}
                        name={id}
                        render={({ field: { onChange, value: formValue } }) => (
                          <MultiSelectInput
                            className="multi-select"
                            label={label}
                            labelPosition="left"
                            menuClassName="dropdown"
                            onChange={(options) =>
                              onChange((options as IOption[]).map(({ value }) => value).toString())
                            }
                            options={options}
                            value={
                              formValue
                                ? options.filter(({ value }) => (formValue as string).split(',').includes(value))
                                : null
                            }
                          />
                        )}
                      />
                    )
                    break
                  case 'NUMBER':
                    input = (
                      <Controller
                        control={control}
                        key={id}
                        name={id}
                        render={() => (
                          <TextInput
                            className="number"
                            defaultValue={value || 0}
                            key={id}
                            label={label}
                            labelPosition="left"
                            placeholder={`Enter ${label}`}
                            type="number"
                            {...register(id)}
                          />
                        )}
                      />
                    )
                    break
                  default:
                    return <div />
                }

                return (
                  <div className="input" key={`${position}-${id}`}>
                    {input}
                  </div>
                )
              })}
          </div>
        ))}
      </section>
    )
  }

  return null
}
